import React, { useState, useEffect, useCallback } from 'react';

/**
 * AdminTradeLog — Complete trade ledger for the admin dashboard.
 *
 * Features:
 *   - Coin filter tabs (All, BTC, ETH, SOL)
 *   - Trades grouped by minute slot
 *   - Each slot: time, prices, outcome, pool size, bet count
 *   - Expandable to show every individual bet
 *   - Load More pagination
 */

const COIN_TABS = [
    { id: null, label: 'All', icon: '◉' },
    { id: 'bitcoin', label: 'BTC', icon: '₿' },
    { id: 'ethereum', label: 'ETH', icon: 'Ξ' },
    { id: 'solana', label: 'SOL', icon: '◎' },
];

const AdminTradeLog = () => {
    const [activeCoin, setActiveCoin] = useState(null);
    const [pools, setPools] = useState([]);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(false);
    const [expandedPool, setExpandedPool] = useState(null);

    const fetchTrades = useCallback(async (coin, before = null, append = false) => {
        setLoading(true);
        try {
            let url = `/api/admin/trades?limit=20`;
            if (coin) url += `&coin=${coin}`;
            if (before) url += `&before=${before}`;

            const res = await fetch(url, { credentials: 'include' });
            if (!res.ok) throw new Error('Failed to fetch');
            const data = await res.json();

            setPools(prev => append ? [...prev, ...data.pools] : data.pools);
            setHasMore(data.hasMore);
        } catch (err) {
            console.error('[AdminTradeLog] Fetch error:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        setPools([]);
        setExpandedPool(null);
        fetchTrades(activeCoin);
    }, [activeCoin, fetchTrades]);

    // Auto-refresh every 30s
    useEffect(() => {
        const interval = setInterval(() => {
            fetchTrades(activeCoin);
        }, 30000);
        return () => clearInterval(interval);
    }, [activeCoin, fetchTrades]);

    const loadMore = () => {
        if (pools.length === 0) return;
        const last = pools[pools.length - 1];
        fetchTrades(activeCoin, last.slotTime, true);
    };

    const formatTime = (iso) => {
        const d = new Date(iso);
        return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    };

    const formatDate = (iso) => {
        const d = new Date(iso);
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    return (
        <div>
            {/* Coin Tabs */}
            <div className="flex gap-1 mb-4 overflow-x-auto pb-1">
                {COIN_TABS.map(tab => (
                    <button
                        key={tab.label}
                        onClick={() => setActiveCoin(tab.id)}
                        className={`px-4 py-2 rounded-lg text-sm uppercase tracking-[0.15em] font-black transition-all shrink-0 ${activeCoin === tab.id
                                ? 'bg-blue-500/15 text-blue-400 border border-blue-500/30'
                                : 'bg-white/[0.02] text-gray-500 border border-white/5 hover:text-gray-300 hover:bg-white/[0.04]'
                            }`}
                    >
                        <span className="mr-1.5">{tab.icon}</span>
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Summary Stats */}
            {pools.length > 0 && (
                <div className="flex gap-4 mb-4">
                    <MiniStat label="Pools Shown" value={pools.length} />
                    <MiniStat label="Total Bets" value={pools.reduce((a, p) => a + p.betCount, 0)} />
                    <MiniStat
                        label="Total Volume"
                        value={`${pools.reduce((a, p) => a + p.totalPool, 0).toLocaleString()} SOL`}
                    />
                    <MiniStat
                        label="Avg Pool"
                        value={`${Math.round(pools.reduce((a, p) => a + p.totalPool, 0) / pools.length).toLocaleString()} SOL`}
                    />
                </div>
            )}

            {/* Pool List */}
            <div className="space-y-1">
                {pools.map(pool => (
                    <PoolRow
                        key={pool.id}
                        pool={pool}
                        expanded={expandedPool === pool.id}
                        onToggle={() => setExpandedPool(expandedPool === pool.id ? null : pool.id)}
                        formatTime={formatTime}
                        formatDate={formatDate}
                    />
                ))}
            </div>

            {/* Empty State */}
            {!loading && pools.length === 0 && (
                <div className="text-center py-16 text-gray-600 text-sm uppercase tracking-widest">
                    No trades found
                </div>
            )}

            {/* Load More */}
            {hasMore && (
                <div className="text-center mt-4">
                    <button
                        onClick={loadMore}
                        disabled={loading}
                        className="px-6 py-2.5 rounded-lg bg-white/[0.03] border border-white/10 text-sm uppercase tracking-[0.2em] font-bold text-gray-400 hover:text-white hover:bg-white/[0.06] transition-all disabled:opacity-50"
                    >
                        {loading ? 'Loading...' : 'Load More'}
                    </button>
                </div>
            )}

            {loading && pools.length === 0 && (
                <div className="text-center py-16 text-gray-600 text-sm uppercase tracking-widest animate-pulse">
                    Loading trades...
                </div>
            )}
        </div>
    );
};

/** Single pool/slot row — expandable to show individual bets. */
const PoolRow = ({ pool, expanded, onToggle, formatTime, formatDate }) => {
    const isUp = pool.priceDiff > 0;
    const isSettled = pool.status === 'settled';

    return (
        <div className="border border-white/5 rounded-lg overflow-hidden">
            {/* Pool Header — click to expand */}
            <button
                onClick={onToggle}
                className="w-full flex items-center gap-4 px-5 py-4 hover:bg-white/[0.02] transition-colors text-left"
            >
                {/* Coin badge */}
                <span className="text-sm uppercase tracking-wider font-black text-gray-500 w-12 shrink-0">
                    {pool.coin.slice(0, 4)}
                </span>

                {/* Time */}
                <span className="font-mono text-sm text-white font-bold w-20 shrink-0">
                    {formatTime(pool.slotTime)}
                </span>
                <span className="text-sm text-gray-600 w-14 shrink-0">
                    {formatDate(pool.slotTime)}
                </span>

                {/* Prices */}
                {isSettled && pool.openPrice > 0 ? (
                    <span className="font-mono text-sm text-gray-400 w-64 shrink-0">
                        <span className="text-blue-400">{pool.openPrice.toFixed(2)}</span>
                        <span className="text-white/30 mx-1">→</span>
                        <span className="text-white">{pool.closePrice.toFixed(2)}</span>
                        <span className={`ml-1.5 ${isUp ? 'text-green-400' : 'text-red-400'}`}>
                            ({isUp ? '+' : ''}{pool.priceDiff.toFixed(2)})
                        </span>
                    </span>
                ) : (
                    <span className="font-mono text-sm text-gray-600 w-64 shrink-0">
                        {pool.status === 'open' ? 'Awaiting...' : 'Locked'}
                    </span>
                )}

                {/* Outcome badge */}
                {isSettled && (
                    <span className={`text-sm font-black uppercase tracking-wider px-3 py-1 rounded w-16 text-center shrink-0 ${pool.outcome === 'UP' ? 'bg-green-500/15 text-green-400' :
                            pool.outcome === 'DOWN' ? 'bg-red-500/15 text-red-400' :
                                'bg-white/10 text-gray-400'
                        }`}>
                        {pool.outcome}
                    </span>
                )}
                {!isSettled && (
                    <span className={`text-sm font-black uppercase tracking-wider px-3 py-1 rounded w-16 text-center shrink-0 ${pool.status === 'open' ? 'bg-blue-500/10 text-blue-400' : 'bg-amber-500/10 text-amber-400'
                        }`}>
                        {pool.status === 'open' ? 'OPEN' : 'LOCK'}
                    </span>
                )}

                {/* Sentiment bar */}
                <div className="flex items-center gap-2 w-28 shrink-0">
                    <div className="flex-1 h-2 bg-white/5 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-gradient-to-r from-green-500 to-green-400 rounded-full"
                            style={{ width: `${pool.upPct}%` }}
                        />
                    </div>
                    <span className="text-sm font-mono text-white w-12 text-right">
                        {pool.upPct}/{100 - pool.upPct}
                    </span>
                </div>

                {/* Pool size */}
                <span className="font-mono text-sm text-white font-bold w-28 text-right shrink-0">
                    {pool.totalPool.toLocaleString()} <span className="text-gray-400">SOL</span>
                </span>

                {/* Bet count */}
                <span className="text-sm text-white w-14 text-right shrink-0">
                    {pool.betCount} bet{pool.betCount !== 1 ? 's' : ''}
                </span>

                {/* Expand arrow */}
                <span className={`text-gray-600 text-sm ml-auto transition-transform ${expanded ? 'rotate-90' : ''}`}>
                    ▶
                </span>
            </button>

            {/* Expanded: Individual Bets */}
            {expanded && pool.bets.length > 0 && (
                <div className="border-t border-white/5">
                    {/* Header */}
                    <div className="flex items-center px-4 py-1.5 bg-white/[0.01] text-sm uppercase tracking-[0.2em] text-gray-600 font-bold">
                        <span className="w-28">User</span>
                        <span className="w-14 text-center">Direction</span>
                        <span className="w-24 text-right">Amount</span>
                        <span className="w-16 text-center">Result</span>
                        <span className="w-24 text-right">Payout</span>
                        <span className="w-20 text-right">Fee</span>
                    </div>

                    {/* Bet rows */}
                    {pool.bets.map(bet => (
                        <div
                            key={bet.id}
                            className="flex items-center px-4 py-2 border-t border-white/[0.03] hover:bg-white/[0.02] transition-colors"
                        >
                            <span className="w-28 text-sm text-gray-300 font-mono truncate">
                                {bet.user}
                            </span>
                            <span className={`w-14 text-center text-sm font-black uppercase ${bet.direction === 'UP' ? 'text-green-400' : 'text-red-400'
                                }`}>
                                {bet.direction}
                            </span>
                            <span className="w-24 text-right font-mono text-sm text-white">
                                {bet.amount.toLocaleString()}
                            </span>
                            <span className="w-16 text-center">
                                {bet.result ? (
                                    <span className={`text-sm font-black uppercase px-1.5 py-0.5 rounded ${bet.result === 'WIN' ? 'bg-green-500/15 text-green-400' :
                                            bet.result === 'LOSS' ? 'bg-red-500/15 text-red-400' :
                                                'bg-white/10 text-gray-400'
                                        }`}>
                                        {bet.result}
                                    </span>
                                ) : (
                                    <span className="text-sm text-gray-600">—</span>
                                )}
                            </span>
                            <span className={`w-24 text-right font-mono text-sm ${bet.result === 'WIN' ? 'text-green-400' :
                                    bet.result === 'LOSS' ? 'text-red-400' :
                                        'text-gray-500'
                                }`}>
                                {bet.payout !== null ? bet.payout.toFixed(2) : '—'}
                            </span>
                            <span className="w-20 text-right font-mono text-sm text-amber-400/60">
                                {bet.fee ? bet.fee.toFixed(2) : '—'}
                            </span>
                        </div>
                    ))}

                    {/* Pool totals */}
                    <div className="flex items-center px-4 py-2 border-t border-white/10 bg-white/[0.02]">
                        <span className="w-28 text-sm text-gray-500 uppercase tracking-wider font-bold">
                            Total
                        </span>
                        <span className="w-14" />
                        <span className="w-24 text-right font-mono text-sm text-white font-bold">
                            {pool.totalPool.toLocaleString()}
                        </span>
                        <span className="w-16" />
                        <span className="w-24 text-right font-mono text-sm text-green-400 font-bold">
                            {pool.bets.filter(b => b.payout).reduce((a, b) => a + b.payout, 0).toFixed(2)}
                        </span>
                        <span className="w-20 text-right font-mono text-sm text-amber-400 font-bold">
                            {pool.bets.filter(b => b.fee).reduce((a, b) => a + b.fee, 0).toFixed(2)}
                        </span>
                    </div>
                </div>
            )}

            {expanded && pool.bets.length === 0 && (
                <div className="px-4 py-6 text-center text-sm text-gray-600 uppercase tracking-wider border-t border-white/5">
                    No bets placed on this pool
                </div>
            )}
        </div>
    );
};

/** Compact stat for the top summary bar. */
const MiniStat = ({ label, value }) => (
    <div className="flex items-center gap-2 px-3 py-1.5 bg-white/[0.02] border border-white/5 rounded-md">
        <span className="text-sm uppercase tracking-wider text-gray-600 font-bold">{label}</span>
        <span className="text-sm font-mono font-bold text-white">{value}</span>
    </div>
);

export default AdminTradeLog;
